// vue router konfiguration
// schützt routen die authentifizierung benötigen und admin-routen
// WICHTIG: prüft auch ob die e-mail verifiziert ist, damit nicht-verifizierte benutzer keinen zugriff bekommen
import { createRouter, createWebHistory } from 'vue-router'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { USE_EMULATOR } from '../config/firebase'

// definition aller routen der anwendung
// jede route hat einen pfad und eine komponente die geladen wird
const routes = [
  { path: '/', component: () => import('../views/LandingPage.vue') }, // startseite
  { path: '/signin', name: 'signin', component: () => import('../views/SignIn.vue') }, // anmeldeseite
  { path: '/register', name: 'register', component: () => import('../views/Register.vue') }, // registrierungsseite
  { path: '/verify-email', component: () => import('../views/VerifyEmail.vue') }, // e-mail-verifizierung
  { path: '/scenarios', component: () => import('../views/ScenariosList.vue'), meta: { requiresAuth: true } }, // szenarien-liste (benötigt login)
  { path: '/dashboard', component: () => import('../views/Dashboard.vue'), meta: { requiresAuth: true } }, // dashboard (benötigt login)
  { path: '/exercise/:id', component: () => import('../views/ExerciseDetail.vue') }, // übungs-detailseite mit id-parameter (öffentlich lesbar)
  { path: '/leaderboard', component: () => import('../views/Leaderboard.vue') }, // bestenliste (öffentlich)
  { path: '/admin', redirect: '/admin/users' }, // redirect zu benutzerverwaltung
  { path: '/admin/users', component: () => import('../views/AdminDashboard.vue'), meta: { requiresAuth: true } }, // admin benutzerverwaltung (benötigt login)
  { path: '/admin/scenarios', component: () => import('../views/AdminDashboard.vue'), meta: { requiresAuth: true } }, // admin szenarien verwalten (benötigt login)
  { path: '/admin/add-scenario', component: () => import('../views/AdminDashboard.vue'), meta: { requiresAuth: true } }, // admin szenario hinzufügen (benötigt login)
  { path: '/impressum', component: () => import('../views/Impressum.vue') }, // impressum (öffentlich)
  { path: '/big-picture', component: () => import('../views/CyberSecurityLandkarte.vue') }, // cyber security landkarte (öffentlich)
]

// erstelle den vue router mit web history (schöne urls ohne #)
export const router = createRouter({
  history: createWebHistory(),
  routes,
  // scroll-verhalten: immer nach oben scrollen beim routenwechsel
  scrollBehavior(to, from, savedPosition) {
    // wenn eine gespeicherte position existiert (z.b. beim zurück-navigieren), verwende diese
    if (savedPosition) {
      return savedPosition
    }
    // sonst immer nach oben scrollen (sofort, ohne animation)
    return { top: 0, behavior: 'instant' }
  }
})

// globaler hook: scroll nach oben nach jeder navigation
// wird nach jedem routenwechsel ausgelöst, auch wenn scrollBehavior nicht greift
router.afterEach(() => {
  // scroll nach oben sofort ohne animation
  window.scrollTo(0, 0)
})

// hilfsfunktion: prüft ob ein benutzer als "vollständig authentifiziert" gilt
// ein benutzer gilt nur als authentifiziert wenn er angemeldet UND seine e-mail verifiziert ist
// im entwicklungsmodus (emulator) wird die e-mail-verifizierung übersprungen
function isFullyAuthenticated(user) {
  if (!user) return false
  if (USE_EMULATOR) return true // im emulator-modus: immer als verifiziert behandeln
  return user.emailVerified === true
}

// auth guard - prüft vor jeder navigation ob der benutzer eingeloggt UND verifiziert ist
// schützt routen die requiresAuth: true haben und admin-routen
router.beforeEach(async (to, from, next) => {
  // wenn benutzer bereits auf signin- oder register-seite ist und angemeldet+verifiziert, weiterleiten
  if (to.path === '/signin' || to.path === '/register') {
    const auth = getAuth()
    // warte auf auth-state, bevor wir prüfen (firebase braucht zeit zum initialisieren)
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe() // beende den listener nach der ersten aktualisierung
        if (isFullyAuthenticated(user)) {
          // benutzer ist angemeldet UND verifiziert, weiterleiten zum dashboard
          next({ path: '/dashboard' })
        } else {
          // benutzer ist nicht angemeldet oder nicht verifiziert, erlaube zugriff auf signin/register
          next()
        }
        resolve()
      })
    })
  }

  // für andere routen
  try {
    const auth = getAuth()
    // prüfe ob die route authentifizierung benötigt (meta.requiresAuth)
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

    // warte auf auth-state, bevor wir prüfen
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe() // beende den listener nach der ersten aktualisierung
        
        // wenn die route auth benötigt aber kein benutzer angemeldet oder nicht verifiziert ist
        if (requiresAuth && !isFullyAuthenticated(user)) {
          // weiterleitung zur signin-seite mit redirect-parameter (damit nach login zurückgeleitet wird)
          next({ name: 'signin', query: { redirect: to.fullPath } })
          resolve()
          return
        }

        // prüfe ob admin-route und ob benutzer admin ist
        if (to.path.startsWith('/admin')) {
          // wenn kein benutzer angemeldet oder nicht verifiziert, weiterleitung zur signin-seite
          if (!isFullyAuthenticated(user)) {
            next({ name: 'signin', query: { redirect: to.fullPath } })
            resolve()
            return
          }
          
          try {
            // prüfe ob benutzer admin ist (hole profil aus firestore)
            const { getCurrentUserProfile } = await import('../services/userService')
            const profile = await getCurrentUserProfile()
            
            // wenn kein profil existiert oder rolle nicht true ist
            if (!profile || profile.role !== true) {
              // nicht-admin versucht auf admin-route zuzugreifen
              // weiterleitung zum dashboard
              next({ path: '/dashboard' })
              resolve()
              return
            }
          } catch (error) {
            console.error('Error checking admin status:', error)
            // bei fehler auch zum dashboard weiterleiten 
            next({ path: '/dashboard' })
            resolve()
            return
          }
        }

        // alle prüfungen bestanden, erlaube navigation
        next()
        resolve()
      })
    })
  } catch (error) {
    console.warn('Auth guard error:', error)
    // bei fehler: leite zur signin-seite weiter (sicherheit geht vor)
    next({ name: 'signin' })
  }
})